import {
  updateObligationPeriodRequestSchema,
  type ObligationPeriod,
  type ObligationPeriodStatus,
} from '@mfos/shared';
import { useState } from 'react';
import { Badge, type BadgeTone } from '../../components/Badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice, LoadingNotice } from '../../components/ErrorNotice';
import { SelectField, TextAreaField, TextField } from '../../components/Field';
import { formatDate, formatDateTime, humanize } from '../../lib/format';
import { blankToNull } from '../../lib/validation';
import { useObligationPeriods, useUpdateObligationPeriod } from './obligations.api';
import { phrase } from '../../lib/i18n/runtime';

const PERIOD_STATUS_DISPLAY: Readonly<
  Record<ObligationPeriodStatus, { label: string; tone: BadgeTone }>
> = {
  NOT_STARTED: { label: 'Not started', tone: 'neutral' },
  ON_TRACK: { label: 'On track', tone: 'info' },
  AT_RISK: { label: 'At risk', tone: 'warn' },
  COMPLETED: { label: 'Completed', tone: 'ok' },
  MISSED: { label: 'Missed', tone: 'down' },
  EXCUSED: { label: 'Excused', tone: 'neutral' },
};

type HolderStatus = 'ON_TRACK' | 'AT_RISK' | 'COMPLETED';
const HOLDER_STATUSES: readonly HolderStatus[] = ['ON_TRACK', 'AT_RISK', 'COMPLETED'];

/**
 * Responsibility periods (spec §6.3, §7.13): what was committed for each period and what happened.
 * A missed period is a recorded fact; any consequence is a separate human governance decision.
 */
export function ObligationPeriodsSection({
  workspaceId,
  founderId,
  timeZone,
}: {
  workspaceId: string;
  founderId: string;
  timeZone: string | undefined;
}) {
  const periods = useObligationPeriods(workspaceId, founderId, {});

  return (
    <section className="card stack" aria-labelledby="obligation-periods-heading">
      <h2 id="obligation-periods-heading">{phrase('Responsibility periods')}</h2>
      <p className="meta">
        Each period of a standing responsibility, with its status history. Missed periods are facts
        for transparency; consequences are decided by the founders, not by the system.
      </p>
      {periods.isPending && <LoadingNotice>{phrase('Loading periods…')}</LoadingNotice>}
      {periods.isError && <ErrorNotice error={periods.error} />}
      {periods.isSuccess && periods.data.items.length === 0 && (
        <p className="meta">{phrase('No responsibility periods in the recent window.')}</p>
      )}
      {periods.isSuccess && periods.data.items.length > 0 && (
        <ul className="item-list">
          {periods.data.items.map((period) => (
            <ObligationPeriodItem
              key={period.id}
              period={period}
              workspaceId={workspaceId}
              timeZone={timeZone}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ObligationPeriodItem({
  period,
  workspaceId,
  timeZone,
}: {
  period: ObligationPeriod;
  workspaceId: string;
  timeZone: string | undefined;
}) {
  const mutation = useUpdateObligationPeriod(workspaceId);
  const [dialog, setDialog] = useState<'status' | 'excuse' | null>(null);
  const [status, setStatus] = useState<HolderStatus>('ON_TRACK');
  const [comment, setComment] = useState('');
  const [evidence, setEvidence] = useState('');
  const display = PERIOD_STATUS_DISPLAY[period.status];
  const evidenceRequired = period.evidenceRequirement === 'REQUIRED';
  const close = () => {
    setDialog(null);
  };
  const open = (next: 'status' | 'excuse') => {
    mutation.reset();
    setStatus(period.status === 'AT_RISK' ? 'AT_RISK' : 'ON_TRACK');
    setComment('');
    setEvidence('');
    setDialog(next);
  };

  return (
    <li className="item">
      <div className="item__header">
        <h3 className="item__title">{period.commitment}</h3>
      </div>
      <div className="badge-row">
        <Badge tone={display.tone} label={display.label} />
        <span className="meta">{humanize(period.category)}</span>
        {evidenceRequired && <span className="meta">· Evidence required</span>}
      </div>
      <dl className="inline-facts">
        <div>
          <dt>{phrase('Period')}</dt>
          <dd>
            {formatDate(period.periodStart)} – {formatDate(period.periodEnd)}
          </dd>
        </div>
        <div>
          <dt>{phrase('Due')}</dt>
          <dd>
            <time dateTime={period.dueAt}>{formatDateTime(period.dueAt, timeZone)}</time>
          </dd>
        </div>
        <div>
          <dt>{phrase('Grace ends')}</dt>
          <dd>
            <time dateTime={period.graceEndsAt}>
              {formatDateTime(period.graceEndsAt, timeZone)}
            </time>
          </dd>
        </div>
        <div>
          <dt>{phrase('Mentioned in updates')}</dt>
          <dd>{period.reportedInUpdates}</dd>
        </div>
      </dl>
      {period.events.length > 0 && (
        <details>
          <summary>Status history ({period.events.length})</summary>
          <ol className="timeline">
            {period.events.map((event, index) => (
              <li key={`${event.occurredAt}:${String(index)}`}>
                <time dateTime={event.occurredAt}>
                  {formatDateTime(event.occurredAt, timeZone)}
                </time>
                {' — '}
                {event.fromStatus !== null
                  ? `${PERIOD_STATUS_DISPLAY[event.fromStatus].label} → `
                  : ''}
                {PERIOD_STATUS_DISPLAY[event.toStatus].label}
                {event.actor !== null ? ` by ${event.actor}` : ' (recorded by the system)'}
                {event.comment !== null && <span className="meta"> · {event.comment}</span>}
                {event.evidenceUrl !== null && (
                  <>
                    {' · '}
                    <a href={event.evidenceUrl} target="_blank" rel="noopener noreferrer">
                      {phrase('Evidence')}
                      <span className="visually-hidden"> (opens in a new tab)</span>
                    </a>
                  </>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
      {(period.canUpdate || period.canExcuse) && (
        <div className="form-actions">
          {period.canUpdate && (
            <button
              type="button"
              className="button button--secondary"
              onClick={() => {
                open('status');
              }}
            >
              {phrase('Update status')}
              <span className="visually-hidden">: {period.commitment}</span>…
            </button>
          )}
          {period.canExcuse && (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                open('excuse');
              }}
            >
              {phrase('Excuse this period')}
              <span className="visually-hidden">: {period.commitment}</span>…
            </button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={dialog === 'status'}
        title={phrase('Update this period')}
        description={`${period.commitment} · ${formatDate(period.periodStart)} – ${formatDate(period.periodEnd)}. Completed is final for the period.`}
        confirmLabel={phrase('Save status')}
        isPending={mutation.isPending}
        error={mutation.error}
        validateExtra={() => {
          const result = updateObligationPeriodRequestSchema.safeParse({
            version: period.version,
            status,
            comment: blankToNull(comment),
            evidenceUrl: blankToNull(evidence),
          });
          if (!result.success) return 'Evidence must be an http(s) link.';
          if (status === 'COMPLETED' && evidenceRequired && evidence.trim() === '') {
            return 'This responsibility requires an evidence link to complete a period.';
          }
          return null;
        }}
        onCancel={close}
        onConfirm={() => {
          mutation.mutate(
            {
              periodId: period.id,
              body: {
                version: period.version,
                status,
                comment: blankToNull(comment),
                evidenceUrl: blankToNull(evidence),
              },
            },
            { onSuccess: close },
          );
        }}
      >
        <SelectField
          label={phrase('Status')}
          value={status}
          onValueChange={(value) => {
            const parsed = HOLDER_STATUSES.find((item) => item === value);
            if (parsed !== undefined) setStatus(parsed);
          }}
          options={HOLDER_STATUSES.map((value) => ({
            value,
            label: PERIOD_STATUS_DISPLAY[value].label,
          }))}
        />
        <TextAreaField
          label={phrase('Comment (optional)')}
          value={comment}
          onValueChange={setComment}
          maxLength={2000}
        />
        <TextField
          label={
            evidenceRequired && status === 'COMPLETED'
              ? phrase('Evidence link')
              : phrase('Evidence link (optional)')
          }
          type="url"
          inputMode="url"
          value={evidence}
          onValueChange={setEvidence}
          required={evidenceRequired && status === 'COMPLETED'}
          maxLength={2048}
        />
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'excuse'}
        title={phrase('Excuse this period?')}
        description={`${period.commitment} · ${formatDate(period.periodStart)} – ${formatDate(period.periodEnd)}. The period will not count as missed. This is final and audited.`}
        confirmLabel={phrase('Excuse period')}
        requireReason
        isPending={mutation.isPending}
        error={mutation.error}
        onCancel={close}
        onConfirm={(reason) => {
          mutation.mutate(
            {
              periodId: period.id,
              body: { version: period.version, status: 'EXCUSED', comment: reason },
            },
            { onSuccess: close },
          );
        }}
      />
    </li>
  );
}
